import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { reduxForm, change } from 'redux-form';
import { get, debounce, map } from 'lodash';
import classnames from 'classnames';
import { BOOKING_REQUEST_FORM } from './constants';
import submitBookingRequestForm from './submit/submitBookingRequestForm';
import validateBookingRequestForm from './validate/validateBookingRequestForm';
import { priceBreakdownObject, showBkgReqFormError } from './bookingUtil';
import RequestBookingDetails from '../RequestBookingDetails';
import RequestAddOns from '../RequestAddOns';
import RequestReview from '../RequestReview';
import { FormattedMessage, injectIntl } from 'react-intl';
import { BookingPriceBreakdown } from '../../elements-summaries/common/items/booking-price-breakdown/BookingPriceBreakdown';
import { BookingSummary } from '../../elements-summaries/booking-summary/BookingSummary';
import { Responsiveness } from '../../elements-responsiveness/responsiveness';
import { StripeElementsProvider } from '../../elements-stripe/context/StripeElementsContext';

class BookingRequestForm extends Component {
  constructor(props) {
    super(props);

    this.getActivityValue = this.getActivityValue.bind(this);
    this.getIdentityValue = this.getIdentityValue.bind(this);
  }

  getActivityValue(value) {
    // Manually set the value of the activity field
    this.props.dispatch(
      change(BOOKING_REQUEST_FORM, 'tags.activity', value.activity)
    );
  }

  getIdentityValue(value) {
    this.props.dispatch(
      change(BOOKING_REQUEST_FORM, 'identity_verification', value)
    );
  }

  render() {
    const {
      booking = {},
      className = '',
      listingData = {},
      handleSubmit = null,
      schedule,
      flowStep,
      spaceRentalContract,
      totalSteps,
      isInstaBookingWithValidAttendees = false,
      activityDetailBooking = {},
      hideFeeCalc = false,
      loadBookingsTrack = null,
      guestNeedsVerification = false,
      alcoholConsumed,
      profile,
      intl,
      masterContract,
      addOnsContracts,
      normalizedContracts,
      selectedPaymentOption,
      handleStripeElementMounted,
      handleExpressCheckoutMethodChange,
      sendRequest
    } = this.props;

    const {
      ownerFirstName,
      ownerAvatarUrl,
      use_type,
      number_guests,
      parentSpaceId,
      host_responsiveness,
      country
    } = listingData;

    const error = get(booking, 'booking.error.body.body.message', '');
    const errorMessage = error
      ? map(error.split('\n'), (item) => (
          <span>
            {item}
            <br />
          </span>
        ))
      : null;

    const reachedMaxDays = 26 <= schedule.length;

    const priceBreakdown = priceBreakdownObject(normalizedContracts);
    const amount = 'pay_partial' === selectedPaymentOption
      ? get(priceBreakdown, 'guestSplitPayment', 0)
      : get(priceBreakdown, 'remainingGuestTotal', 0)
    const currency = get(priceBreakdown, 'currency', '')

    const pageDetails = {
      1: {
        header: this.props.intl.formatMessage({
          id: 'booking_request.booking_request_form.booking_details_header',
          defaultMessage: 'Request to Book'
        }),
        component: (
          <div className='request-booking-details-wrap'>
            <RequestBookingDetails
              {...this.props}
              spaceId={parentSpaceId}
              reachedMaxDays={reachedMaxDays}
              listingUseType={use_type}
              getActivityValue={this.getActivityValue}
              numberGuests={number_guests}
              errorMessage={errorMessage}
              useType={use_type}
              loadBookingsTrack={loadBookingsTrack}
            />
          </div>
        )
      },
      ...(3 === totalSteps && {
        2: {
          header: this.props.intl.formatMessage({
            id: 'booking_request.booking_request_form.add_ons_header',
            defaultMessage: 'Select Add-ons'
          }),
          component: (
            <RequestAddOns
              {...this.props}
              formName={BOOKING_REQUEST_FORM}
              spaceRental={spaceRentalContract}
            />
          )
        }
      }),
      [totalSteps]: {
        header: this.props.intl.formatMessage({
          id: 'booking_request.booking_request_form.review_header',
          defaultMessage: 'Review & Pay'
        }),
        component: (
          <StripeElementsProvider
            country={country}
            currency={currency}
            amount={amount}
            onElementsMounted={handleStripeElementMounted}
            onWalletChange={handleExpressCheckoutMethodChange}
            onSubmit={sendRequest}
          >
            <RequestReview
              {...this.props}
              ownerFirstName={ownerFirstName}
              ownerAvatarUrl={ownerAvatarUrl}
              listingData={listingData}
              guestNeedsVerification={guestNeedsVerification}
              getIdentityValue={this.getIdentityValue}
              alcoholConsumed={alcoholConsumed}
            />
          </StripeElementsProvider>
        )
      }
    };

    const displayIBTitle = isInstaBookingWithValidAttendees && 1 === flowStep;
    return (
      <form
        data-testing-id='booking-request-form'
        className={classnames('BookingRequestForm form-horizontal', className)}
        onSubmit={handleSubmit}
      >
        <div className='header margin-xl-bottom'>
          <h2
            className={classnames({ 'IB-title': displayIBTitle }, 'h3 no-bottom-margin')}
            data-testing-id='request-header'
          >
            {displayIBTitle ? (
              <span>
                <img
                  style={{
                    paddingRight: '8px',
                    height: '26px',
                    verticalAlign: 'sub',
                    marginBottom: 4
                  }}
                  src='/web-2/assets/images/lightningbolt.svg?v=rb'
                  alt='lightning bolt'
                />
                <FormattedMessage
                  id='booking_request.booking_request_form.instant_booking'
                  defaultMessage='Instant Booking'
                />
              </span>
            ) : (
              pageDetails[flowStep].header
            )}
          </h2>
          {!isInstaBookingWithValidAttendees &&
            flowStep === totalSteps &&
            host_responsiveness && (
              <p>
                <Responsiveness
                  useGutter={false}
                  ownerName={ownerFirstName}
                  noTooltip
                  level={host_responsiveness.level || 0}
                  bucket={host_responsiveness.bucket}
                  responseTime={host_responsiveness.hours}
                  labelClassName='responsiveness-disclaimer'
                />
              </p>
            )}
        </div>

        {flowStep === totalSteps && (
          <div className='activity-details margin-xl-top margin-lg-bottom d-md-none'>
            <BookingSummary
              booking={activityDetailBooking}
              profile={profile}
              intl={intl}
            />
          </div>
        )}

        {pageDetails[flowStep].component}
      </form>
    );
  }
}

const config = {
  form: BOOKING_REQUEST_FORM,
  onSubmit: debounce(submitBookingRequestForm, 250),
  validate: validateBookingRequestForm,
  onSubmitFail: showBkgReqFormError,
  shouldValidate: () => true
};

BookingRequestForm.propTypes = {
  className: PropTypes.string,
  listingData: PropTypes.object,
  isSubmitting: PropTypes.bool,
  getFeeCalc: PropTypes.func,
  booking: PropTypes.object,
  handleSubmit: PropTypes.func,
  dispatch: PropTypes.func,
  spaceRentalContract: PropTypes.object,
  isInstaBookingWithValidAttendees: PropTypes.bool,
  activityDetailBooking: PropTypes.object,
  loadBookingsTrack: PropTypes.func,
  splitPaymentPermitted: PropTypes.bool
};

export default injectIntl(reduxForm(config)(BookingRequestForm));
